import React from 'react';
import { DateRangePicker, DateRangePickerProps } from 'rsuite';
import { Control, Controller, RegisterOptions, useController } from 'react-hook-form';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Correct from 'shared/ui/icons/General/Correct';
import Wrong from 'shared/ui/icons/General/Wrong';
import Edit from 'shared/ui/icons/General/Edit';
import Select from 'shared/ui/Select/Select';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import { getInputState } from 'shared/ui/Input/inputHelper';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';
import {brandColor} from 'shared/lib/colors';

import styles from './DateRangePickerControlled.module.scss';

export interface IAutoSelectDateRangePickerProps extends DateRangePickerProps {
    control: Control<any>;
    name: string;
    rules?: RegisterOptions;
    validate?: boolean;
    minDate?: Date;
    // eslint-disable-next-line no-unused-vars
    shouldDisableDate?: (date: Date) => boolean;
    showTimeSelects?: boolean;
    startTimeValue?: string;
    endTimeValue?: string;
    // eslint-disable-next-line no-unused-vars
    onTimeChange?: (type: 'start' | 'end', time: string) => void;
}

const AutoSelectDateRangePicker: React.FC<IAutoSelectDateRangePickerProps> = ({
    control,
    name,
    rules,
    validate = true,
    defaultValue,
    onChange,
    placeholder = t`Выберите диапазон дат...`,
    minDate,
    showTimeSelects = true,
    startTimeValue = '00:00',
    endTimeValue = '23:59',
    onTimeChange,
    ...rest
}) => {
    const { field, fieldState } = useController({
        name,
        control,
        defaultValue,
    });
    const startTime = startTimeValue;
    const endTime = endTimeValue;

    const state = getInputState({
        fieldState,
        value: field.value,
        validate,
    });

    const dateFormat = !isTranslate() ? 'dd.MM.yyyy' : 'yyyy-MM-dd';

    const iconsMap = {
        valid: <Correct color={brandColor(PALETTE.accent, PALETTE.success)}/>,
        error: <Wrong />,
        filled: <Edit />,
        default: <Edit />,
    };

    const classMap = isTranslate()
        ? {
            valid: styles.enItem_valid,
            error: styles.enItem_error,
            filled: styles.enItem_filled,
            default: styles.enItem_default,
        }
        : {
            valid: styles.item_valid,
            error: styles.item_error,
            filled: styles.item_filled,
            default: styles.item_default,
        };

    const baseClass = isTranslate() ? styles.enItem : styles.item;

    const shouldDisableDate = (date: Date) => {
        if (minDate) {
            return date < minDate;
        }
        return false;
    };

    const timeOptions = React.useMemo(() => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push({ value: timeString, label: timeString });
            }
        }
        return options;
    }, []);

    const applyTimeToDates =
        (dates: any[] | null, currentStartTime: string, currentEndTime: string): [Date, Date] | null => {
            if (!dates || !Array.isArray(dates) || dates.length !== 2 || !dates[0] || !dates[1]) {
                return null;
            }

            const [startDate, endDate] = dates;

            const [startHours, startMinutes] = currentStartTime.split(':').map(Number);
            const newStartDate = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                startHours,
                startMinutes,
                0,
                0,
            );

            const [endHours, endMinutes] = currentEndTime.split(':').map(Number);
            const newEndDate = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                endHours,
                endMinutes,
                59,
                999,
            );

            return [newStartDate, newEndDate];
        };

    const handleSelect = (date: Date) => {
        if (!date || isNaN(date.getTime())) return;

        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);

        const startOfDay = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            startHours,
            startMinutes,
            0,
            0,
        ));

        const endOfDay = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            endHours,
            endMinutes,
            59,
            999,
        ));

        if (isNaN(startOfDay.getTime()) || isNaN(endOfDay.getTime())) return;

        const newValue: [Date, Date] = [startOfDay, endOfDay];
        field.onChange(newValue);

        if (onChange) {
            onChange(newValue, {} as any);
        }
    };
    const handleTimeChange = (type: 'start' | 'end', time: string | number | null) => {
        const timeString = time as string;

        if (onTimeChange) {
            onTimeChange(type, timeString);
        }

        const newStartTime = type === 'start' ? timeString : startTime;
        const newEndTime = type === 'end' ? timeString : endTime;

        const currentValue = field.value;
        if (currentValue && Array.isArray(currentValue) && currentValue.length === 2) {
            const updatedValue = applyTimeToDates(currentValue, newStartTime, newEndTime);
            if (updatedValue) {
                field.onChange(updatedValue);
                if (onChange) {
                    onChange(updatedValue, {} as any);
                }
            }
        }
    };

    return (
        <div>
            <div className={styles.item_container}>
                <div className={styles.icon}>
                    {iconsMap[state]}
                </div>
                <Controller
                    control={control}
                    name={name}
                    rules={rules}
                    defaultValue={defaultValue}
                    render={({ field: { onChange: onChangeFrom, ...field } }) => (
                        <DateRangePicker
                            onChange={(value, event) => {
                                if (value && Array.isArray(value) && value.length === 2) {
                                    const valueWithTime = applyTimeToDates(value, startTime, endTime);
                                    if (valueWithTime) {
                                        onChangeFrom(valueWithTime);
                                        onChange && onChange(valueWithTime, event);
                                    }
                                }
                            }}
                            onSelect={handleSelect}
                            menuClassName={isTranslate() ? 'english-calendar-menu' : ''}
                            className={cn(
                                baseClass,
                                classMap[state],
                            )}
                            placeholder={placeholder}
                            format={dateFormat}
                            showOneCalendar
                            preventOverflow
                            shouldDisableDate={shouldDisableDate}
                            isoWeek
                            oneTap
                            ranges={[]}
                            {...rest}
                            {...field}
                        />
                    )}
                />
            </div>

            {showTimeSelects && (
                <div className={styles.timeSelects}>
                    <FormGroupWithError label={t`Время начала`} className={styles.select}>
                        <Select
                            control={control}
                            name="startTime"
                            data={timeOptions}
                            cleanable={false}
                            searchable={false}
                            placeholder="00:00"
                            onChange={(value) => handleTimeChange('start', value)}
                            value={startTime}
                            hideIcon={true}
                        />
                    </FormGroupWithError>
                    <FormGroupWithError label={t`Время окончания`} className={styles.select}>
                        <Select
                            control={control}
                            name="endTime"
                            data={timeOptions}
                            cleanable={false}
                            searchable={false}
                            placeholder="23:59"
                            onChange={(value) => handleTimeChange('end', value)}
                            value={endTime}
                            hideIcon={true}
                        />
                    </FormGroupWithError>
                </div>
            )}
        </div>
    );
};

export default AutoSelectDateRangePicker;